import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import LeaseUpCalculator from "./LeaseUpCalculator";
import CompMapModal from "./CompMapModal";
import { refreshValues as cadRefreshValues, getSupportedCads as cadGetSupportedCads } from "../cadApi";
import RefreshValuesModal from "../components/RefreshValuesModal";

/* ----------------- CSV + optional XLSX parsing ----------------- */
function splitCsvLine(line) {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQ = !inQ;
      }
    } else if (ch === "," && !inQ) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
function parseCsv(text) {
  const lines = String(text)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((l) => l.trim() !== "");
  if (!lines.length) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0] || "");
  const rows = lines.slice(1).map((ln) => splitCsvLine(ln));
  return { headers, rows };
}
async function parseXlsx(file) {
  if (!window.XLSX) {
    throw new Error("XLSX parser not found. Make sure window.XLSX is available.");
  }
  const buf = await file.arrayBuffer();
  const wb = window.XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = window.XLSX.utils.sheet_to_json(ws, { header: 1 });
  const headers = (rows[0] || []).map((c) => String(c ?? ""));
  const out = rows.slice(1).map((r) => r.map((c) => (c == null ? "" : String(c))));
  return { headers, rows: out };
}

/* ----------------- tiny helpers ----------------- */
const num = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[$,%\s,]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};
const pctToDec = (v) => {
  const s = String(v ?? "").trim().replace(/%/g, "");
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n / 100 : 0;
};
const fmtMoney0 = (v) =>
  v == null || isNaN(v) ? "—" : `$${Math.round(Number(v)).toLocaleString()}`;
const fmtMoney2 = (v) =>
  v == null || isNaN(v)
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtMult = (x) =>
  x && isFinite(x)
    ? `${Number(x).toLocaleString(undefined, { maximumFractionDigits: 2 })}×`
    : "—";
const median = (arr) => {
  const xs = arr.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b);
  if (xs.length === 0) return 0;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
};
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Math.random().toString(36).slice(2, 8)}`);

/* ----------------- auto-map headers for EU-Income ----------------- */
const FIELD_DEFS_INC = [
  { key: "account", label: "Account #", synonyms: ["account", "acct", "parcel", "id", "account number", "account #"] },
  { key: "site_name", label: "Site Name", synonyms: ["site name", "property", "property name", "asset", "center name", "store name"] },
  { key: "address", label: "Address", synonyms: ["address", "site address", "situs", "location", "street", "full address", "addr"] },
  { key: "property_type", label: "Property Type", synonyms: ["property type", "use", "type", "ptype", "prop type", "category", "group", "group code"] },
  { key: "class", label: "Class", synonyms: ["class", "quality", "grade", "building class", "quality class", "grade class"] },
  { key: "size", label: "Size / Units", synonyms: ["size", "units", "unit count", "sf", "sqft", "area", "gla", "bldg sf", "building sf", "net rentable"] },
  { key: "year_built", label: "Year Built", synonyms: ["year built", "yr built", "year", "built"] },
  { key: "effective_year_built", label: "Effective YB", synonyms: ["effective year built", "effective yb", "eyb", "eff year", "eff year built"] },
  { key: "nbhd", label: "IMA / Neighborhood", synonyms: ["ima", "ima code", "neighborhood", "nbhd", "nbhd code", "location code", "loc code", "cad location code", "market area", "neighborhood code"] },
  { key: "market_value", label: "Total Value", synonyms: ["total value", "appraised value", "market value", "value", "total appraised", "assessed value"] },
  // optional income inputs (per-property; only used when universal flags are off)
  { key: "rent_per_unit_month", label: "Rent / Unit (month)", synonyms: ["rent", "monthly rent", "rent per unit", "rent per sf", "rent / unit / month", "monthly rent per unit", "monthly rent per sf"] },
  { key: "vacancy_pct", label: "Vacancy %", synonyms: ["vacancy", "vacancy %", "vacancy percent", "vacancy rate", "econ vacancy", "economic vacancy"] },
  { key: "opex_mode", label: "Opex Mode", synonyms: ["opex mode", "expense mode", "exp mode"] },
  { key: "opex_per_unit_annual", label: "Opex $/Unit/yr", synonyms: ["opex per unit annual", "opex $/unit/yr", "opex per unit", "expenses per unit", "opex / unit", "operating expenses per unit"] },
  { key: "opex_pct", label: "Opex % of EGI", synonyms: ["opex %", "expense %", "expenses % egi", "opex % egi", "operating expense %", "expense ratio"] },
  { key: "notes", label: "Notes", synonyms: ["notes", "remarks", "comment", "comments"] },
  { key: "is_subject", label: "Subject?", synonyms: ["subject", "is subject", "role"] },
];
const INDEX_BY_KEY_INC = Object.fromEntries(
  FIELD_DEFS_INC.map((f, i) => [f.key, i])
);

function autoMapInc(headers) {
  const map = {};
  headers.forEach((raw, idx) => {
    const h = String(raw || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    let best = null;
    let score = 0;
    for (const f of FIELD_DEFS_INC) {
      for (const candidate of [f.label, ...(f.synonyms || [])]) {
        const c = String(candidate).toLowerCase();
        if (!c) continue;
        if (h === c) {
          best = f.key;
          score = 100;
          break;
        }
        if (h.includes(c)) {
          if (score < 50) {
            best = f.key;
            score = 50;
          }
        }
      }
      if (score === 100) break;
    }
    map[idx] = best || null;
  });
  return map;
}

/* ----------------- small UI bits ----------------- */
function Checkbox({ checked, onChange, disabled, title }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-slate-300"
      checked={!!checked}
      onChange={(e) => onChange?.(e.target.checked)}
      disabled={disabled}
      title={title}
    />
  );
}
function NumberCell({
  value,
  onChange,
  suffix,
  className,
  disabled,
  inputMode = "text",
  placeholder,
}) {
  return (
    <div className={`flex items-center ${className || ""}`}>
      <input
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        inputMode={inputMode}
        placeholder={placeholder}
      />
      {suffix && <span className="ml-1 text-xs text-slate-500">{suffix}</span>}
    </div>
  );
}

/* ----------------- refresh results dialog ----------------- */
function RefreshResultsDialog({ result, onClose, onUncheckFailed }) {
  if (!result) return null;

  const failed = Array.isArray(result.failed) ? result.failed : [];
  const hasFailed = failed.length > 0;
  const isComplete = result.variant === "complete";
  const isError = result.variant === "error";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="w-[520px] max-w-[95vw] max-h-[85vh] flex flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div
            className={`h-9 w-9 rounded-full flex items-center justify-center text-lg ${
              isError
                ? "bg-rose-100 text-rose-700"
                : hasFailed
                ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
            aria-hidden
          >
            {isError ? "!" : hasFailed ? "⚠" : "✓"}
          </div>
          <h3 className="text-lg font-semibold text-slate-800">{result.title}</h3>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {isComplete && (
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Updated
                </div>
                <div className="mt-1 text-2xl font-bold text-emerald-800">
                  {result.updated ?? 0}
                </div>
              </div>
              <div
                className={`rounded-lg border p-3 ${
                  hasFailed
                    ? "border-rose-200 bg-rose-50"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    hasFailed ? "text-rose-700" : "text-slate-500"
                  }`}
                >
                  Errors
                </div>
                <div
                  className={`mt-1 text-2xl font-bold ${
                    hasFailed ? "text-rose-800" : "text-slate-700"
                  }`}
                >
                  {failed.length}
                </div>
              </div>
            </div>
          )}

          {hasFailed && (
            <div className="mb-4">
              <div className="text-sm font-semibold text-slate-700 mb-1">
                Failed Accounts
              </div>
              <div className="text-xs text-slate-500 mb-2">
                These accounts could not be refreshed. You can uncheck them
                from use below.
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 border-b border-slate-200">
                          Account
                        </th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600 border-b border-slate-200">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {failed.map((f, i) => (
                        <tr key={`${f.account}-${i}`} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono text-xs text-slate-800">
                            {f.account}
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">
                            {f.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {result.harrisNote && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-sm font-semibold text-amber-800 mb-1">
                Harris County Note
              </div>
              <p className="text-sm text-amber-700">{result.harrisNote}</p>
            </div>
          )}

          {result.message && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">
              {result.message}
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          {hasFailed && onUncheckFailed && (
            <button
              type="button"
              onClick={() => onUncheckFailed(failed.map((f) => f.account))}
              className="px-3 py-1.5 rounded-md border border-rose-300 bg-rose-50 text-rose-800 text-sm font-medium hover:bg-rose-100"
            >
              Uncheck {failed.length} Failed {failed.length === 1 ? "Row" : "Rows"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- Mapping dialog for EU-Income ----------------- */
function MappingDialogInc({
  open,
  headers,
  sampleRows,
  unitNoun,
  initialFieldToIndex,
  onCancel,
  onImport,
}) {
  const FIELDS_TO_SHOW = [
    "account",
    "site_name",
    "address",
    "property_type",
    "class",
    "size",
    "year_built",
    "effective_year_built",
    "nbhd",
    "market_value",
    "rent_per_unit_month",
    "vacancy_pct",
    "opex_mode",
    "opex_per_unit_annual",
    "opex_pct",
    "notes",
    "is_subject",
  ];
  const [fieldToIndex, setFieldToIndex] = useState(initialFieldToIndex || {});
  const [subjectMode, setSubjectMode] = useState("auto"); // "auto" | "firstrow"

  useEffect(() => {
    setFieldToIndex(initialFieldToIndex || {});
  }, [initialFieldToIndex, open]);

  if (!open) return null;

  const first3 = sampleRows.slice(0, 3);
  const required = ["size", "market_value"];
  const missingRequired = required.filter(
    (k) => fieldToIndex[k] == null || fieldToIndex[k] < 0
  );
  const disableImport = missingRequired.length > 0;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
      <div className="w-[760px] max-w-[95vw] rounded-xl border border-slate-200 bg-white shadow-lg">
        <div className="px-4 py-3 border-b">
          <div className="font-semibold">Map Columns (EU - Income)</div>
          <div className="text-xs text-slate-500">
            We guessed based on your headers; tweak if needed, then Import.
          </div>
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          {FIELDS_TO_SHOW.map((key) => {
            const def = FIELD_DEFS_INC[INDEX_BY_KEY_INC[key]];
            const idx = fieldToIndex[key] ?? -1;
            return (
              <div key={key} className="grid grid-cols-5 gap-2 items-center">
                <div className="text-sm col-span-2">
                  <div className="font-medium">{def?.label}</div>
                  {key === "size" && (
                    <div className="text-xs text-slate-500">
                      Interpreted as{" "}
                      {unitNoun === "SF" ? "SF" : unitNoun}
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    value={idx}
                    onChange={(e) =>
                      setFieldToIndex((p) => ({
                        ...p,
                        [key]: Number(e.target.value),
                      }))
                    }
                  >
                    <option value={-1}>(not mapped)</option>
                    {headers.map((h, i) => (
                      <option key={i} value={i}>
                        {h || `(column ${i + 1})`}
                      </option>
                    ))}
                  </select>
                  {idx >= 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 truncate">
                      {first3
                        .map((r) => String(r[idx] ?? ""))
                        .filter(Boolean)
                        .slice(0, 2)
                        .join(" · ") || "—"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div className="pt-2 border-t">
            <div className="text-sm font-medium mb-1">Subject row</div>
            <label className="mr-3 text-sm">
              <input
                type="radio"
                name="euinc-subjmode"
                checked={subjectMode === "auto"}
                onChange={() => setSubjectMode("auto")}
                className="mr-1"
              />
              Auto-detect (match account/site name/address to Cover)
            </label>
            <label className="text-sm">
              <input
                type="radio"
                name="euinc-subjmode"
                checked={subjectMode === "firstrow"}
                onChange={() => setSubjectMode("firstrow")}
                className="mr-1"
              />
              First row is Subject
            </label>

            {disableImport && (
              <div className="mt-2 text-xs text-rose-600">
                Please map at least <b>Size</b> and <b>Total Value</b> before importing.
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 border border-slate-300 bg-white"
          >
            Cancel
          </button>
          <button
            onClick={() => onImport(fieldToIndex, subjectMode)}
            disabled={disableImport}
            className={`rounded-lg px-3 py-1.5 border ${
              disableImport
                ? "border-slate-200 bg-slate-100 text-slate-400"
                : "border-sky-300 bg-sky-50 text-sky-800 hover:border-sky-400"
            }`}
          >
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- Section ----------------- */
export default function EqualUniformIncomeSection({ packet, data, onChange, onSave }) {
  const initial = data?.equal_uniform_income || {};

  // ----- DEFAULT COLUMNS (persisted via config.columns) -----
  const DEFAULT_COLUMNS = {
    use: true,
    account: true,
    site_name: true,
    address: true,
    property_type: true,
    class: true,
    size: true,
    year_built: true,
    effective_year_built: true,
    nbhd: true,
    market_value: true,
    assessed_per_unit: true,
    noi: true,
    value_over_noi: true,
    notes: true,
  };

  const [rows, setRows] = useState(Array.isArray(initial.rows) ? initial.rows : []);
  const [config, setConfig] = useState(() => ({
    subjectId: initial.config?.subjectId || null,
    columns: { ...DEFAULT_COLUMNS, ...(initial.config?.columns || {}) },
    baseline: {
      // baseline always exists; universal flags decide whether rows can override
      rent_per_unit_month: initial.config?.baseline?.rent_per_unit_month ?? "",
      vacancy_pct: initial.config?.baseline?.vacancy_pct ?? "5%",
      opex_mode: initial.config?.baseline?.opex_mode || "per_unit", // 'per_unit' | 'pct_egi'
      opex_per_unit_annual: initial.config?.baseline?.opex_per_unit_annual ?? "",
      opex_pct: initial.config?.baseline?.opex_pct ?? "",
      // universal flags
      universal: {
        rent: initial.config?.baseline?.universal?.rent ?? true,
        vacancy: initial.config?.baseline?.universal?.vacancy ?? true,
        opex: initial.config?.baseline?.universal?.opex ?? true,
      },
    },
  }));
  const [showPicker, setShowPicker] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  // Comp selection criteria
  const [compCriteria, setCompCriteria] = useState(() => ({
    nbhd_area: "",
    radius: "",
    type: "",
    age_min: "",
    age_max: "",
    size_min: "",
    size_max: "",
    class: "",
    none_omitted: false,
    ...(initial.comp_criteria || {}),
  }));
  const updateCriteria = (patch) => setCompCriteria((p) => ({ ...p, ...patch }));

  // Comp map state
  const [compMapOpen, setCompMapOpen] = useState(false);
  const [compMapData, setCompMapData] = useState(initial.map_data || null);

  // NEW: dynamic supported CAD list for value scraping tooltip/popup
  const [supportedCads, setSupportedCads] = useState([]);
  const [showCadInfo, setShowCadInfo] = useState(false);

  useEffect(() => {
    let alive = true;
    cadGetSupportedCads()
      .then((list) => {
        if (!alive) return;
        setSupportedCads(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // silent: tooltip/popup can still work without list
      });
    return () => {
      alive = false;
    };
  }, []);

  // NEW: mapping dialog state for import
  const [mapUI, setMapUI] = useState({
    open: false,
    headers: [],
    sample: [],
    guessed: {},
  });
  const [pendingRows, setPendingRows] = useState([]);

  // ---------- NEW: Deductions state ----------
  const [deductionType, setDeductionType] = useState(
    (initial.deduction_type || "none").toLowerCase()
  );
  const [leaseUp, setLeaseUp] = useState(initial.lease_up || null);
  const [manualDeduction, setManualDeduction] = useState(
    initial.manual_deduction ?? ""
  );

  // -------- Cover context (for seeding + unit labels) --------
  const coverCtx = useMemo(() => {
    let latestCover = null,
      maxOrder = -Infinity;
    for (const s of packet?.sections || []) {
      if (String(s?.type || "").toLowerCase() !== "cover") continue;
      const oi = s?.order_index ?? 0;
      if (oi > maxOrder) {
        maxOrder = oi;
        latestCover = s;
      }
    }
    const coverMeta = latestCover?.data || {};
    const pi = coverMeta?.property_info || {};
    const unitType = (pi.unit_type || "").toString().toUpperCase() || "UNITS";
    const sizeFromCover =
      Number(
        String(pi.units ?? pi.sqft ?? pi.area_sf ?? "").replace(/[$,%\s,]/g, "")
      ) || 0;

    // ✅ Year comes ONLY from packet header
    const taxYear =
      Number(String(packet?.tax_year ?? "").replace(/\D/g, "")) ||
      new Date().getFullYear();

    return {
      account: pi.account || pi.parcel || "",
      site_name: pi.site_name || "",
      address: pi.address || "",
      property_type: pi.property_type || "",
      year_built: pi.year_built || "",
      effective_year_built: pi.effective_year_built || pi.eyb || "",
      nbhd: pi.cad_location_code || "",
      market_value: pi.current_year_value || pi.appraised_value || "",
      unitType,
      sizeFromCover,
      taxYear,
      coverMeta, // keep sending to backend for CAD auto-detect
    };
  }, [packet]);
  const unitNoun =
    coverCtx.unitType === "SF"
      ? "SF"
      : coverCtx.unitType === "BEDS"
      ? "Bed"
      : "Unit";
  const rentLabel =
    coverCtx.unitType === "SF"
      ? "Monthly Rent / SF"
      : `Monthly Rent / ${unitNoun}`;
  const perUnitLabel =
    coverCtx.unitType === "SF" ? "$ / SF" : `$ / ${unitNoun}`;

  // --------- Import helpers (inside component to capture coverCtx/rows/config) ----------
  function toFieldToIndexInc(guessedHeaderIdxToField) {
    const fieldToIndex = {};
    for (const [colIdx, key] of Object.entries(guessedHeaderIdxToField || {})) {
      if (key) fieldToIndex[key] = Number(colIdx);
    }
    return fieldToIndex;
  }

  function mapRowFromFieldToIndexInc(cells, f2i) {
    const get = (key) => {
      const idx = f2i[key];
      return idx == null || idx < 0 ? "" : cells[idx] ?? "";
    };

    const row = {
      id: uid(),
      use: true,
      account: get("account"),
      site_name: get("site_name"),
      address: get("address"),
      property_type: get("property_type"),
      class: get("class"),
      size: get("size"),
      year_built: get("year_built"),
      effective_year_built:
        get("effective_year_built") || get("eyb"),
      nbhd: get("nbhd"),
      market_value: get("market_value"),
      // property-specific income fields (optional)
      ps_rent_per_unit_month: get("rent_per_unit_month"),
      ps_vacancy_pct: get("vacancy_pct"),
      ps_opex_mode: get("opex_mode") || "per_unit",
      ps_opex_per_unit_annual: get("opex_per_unit_annual"),
      ps_opex_pct: get("opex_pct"),
      notes: get("notes"),
    };

    // Hint: subject match against Cover (account or site name/address)
    row._matchCover =
      (!!coverCtx.account &&
        String(row.account).trim() ===
          String(coverCtx.account).trim()) ||
      (!!coverCtx.site_name &&
        String(row.site_name || "")
          .toLowerCase()
          .includes(String(coverCtx.site_name || "").toLowerCase())) ||
      (!!coverCtx.address &&
        String(row.address || "")
          .toLowerCase()
          .includes(String(coverCtx.address || "").toLowerCase()));

    row._explicitSubject = /^y(es)?|true|subject$/i.test(
      String(get("is_subject"))
    );

    return row;
  }

  async function openFileAndPrepareMapping(file) {
    let parsed = { headers: [], rows: [] };
    const ext = (file.name.split(".").pop() || "").toLowerCase();

    if (ext === "xlsx") {
      try {
        parsed = await parseXlsx(file);
      } catch (e) {
        alert(
          e?.message ||
            "Failed to parse Excel. Ensure window.XLSX is available."
        );
        return;
      }
    } else {
      const text = await file.text();
      parsed = parseCsv(text);
    }

    const guessed = autoMapInc(parsed.headers);
    setMapUI({
      open: true,
      headers: parsed.headers,
      sample: parsed.rows.slice(0, 10),
      guessed,
    });
    setPendingRows(parsed.rows);
  }

  function confirmImport(fieldToIndex, subjectMode) {
    const required = ["size", "market_value"];
    const missing = required.filter(
      (k) => fieldToIndex[k] == null || fieldToIndex[k] < 0
    );
    if (missing.length) {
      alert(
        `Please map the following fields before importing: ${missing.join(
          ", "
        )}`
      );
      return;
    }

    const newRowsRaw = (pendingRows || []).map((cells) =>
      mapRowFromFieldToIndexInc(cells, fieldToIndex)
    );
    if (!newRowsRaw.length) {
      setMapUI({ open: false, headers: [], sample: [], guessed: {} });
      setPendingRows([]);
      return;
    }

    // choose subject
    let subj = null;
    if (subjectMode === "firstrow") {
      subj = newRowsRaw[0];
    } else {
      subj =
        newRowsRaw.find((r) => r._explicitSubject || r._matchCover) ||
        newRowsRaw[0];
    }
    delete subj._explicitSubject;
    delete subj._matchCover;

    // imported grid: subject first (use=false), then comps (use=true)
    const imported = [
      subj,
      ...newRowsRaw
        .filter((r) => r !== subj)
        .map((r) => ({ ...r, use: true })),
    ];

    setRows(imported.map((r) => (r === subj ? { ...r, use: false } : r)));
    setConfig((prev) => ({ ...prev, subjectId: subj.id }));

    setMapUI({ open: false, headers: [], sample: [], guessed: {} });
    setPendingRows([]);
  }

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    openFileAndPrepareMapping(f);
    e.target.value = "";
  }

  // Seed one Subject row from Cover if empty
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!rows || rows.length === 0) {
      const subjectRow = {
        id: uid(),
        use: false,
        account: coverCtx.account || "",
        site_name: coverCtx.site_name || "",
        address: coverCtx.address || "",
        property_type: coverCtx.property_type || "",
        class: "",
        size: coverCtx.sizeFromCover ? String(coverCtx.sizeFromCover) : "",
        year_built: coverCtx.year_built || "",
        effective_year_built: coverCtx.effective_year_built || "",
        nbhd: coverCtx.nbhd || "",
        market_value: coverCtx.market_value || "",
        // property-specific income fields (used only when universal flag for that criterion is OFF)
        ps_rent_per_unit_month: "",
        ps_vacancy_pct: "",
        ps_opex_mode: "per_unit",
        ps_opex_per_unit_annual: "",
        ps_opex_pct: "",
        notes: "",
      };
      setRows([subjectRow]);
      setConfig((p) => ({ ...p, subjectId: subjectRow.id }));
      seededRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(coverCtx), rows?.length]);

  // Helpers to get active inputs (mix of universal vs per-row)
  const isUniversal = useCallback(
    (k) => !!config.baseline.universal?.[k],
    [config.baseline.universal]
  );
  const paramsForRow = useCallback(
    (r) => {
      return {
        rent_per_unit_month: isUniversal("rent")
          ? config.baseline.rent_per_unit_month
          : r.ps_rent_per_unit_month ?? "",
        vacancy_pct: isUniversal("vacancy")
          ? config.baseline.vacancy_pct
          : r.ps_vacancy_pct ?? "",
        opex_mode: isUniversal("opex")
          ? config.baseline.opex_mode
          : r.ps_opex_mode || "per_unit",
        opex_per_unit_annual: isUniversal("opex")
          ? config.baseline.opex_per_unit_annual
          : r.ps_opex_per_unit_annual ?? "",
        opex_pct: isUniversal("opex")
          ? config.baseline.opex_pct
          : r.ps_opex_pct ?? "",
      };
    },
    [config.baseline, isUniversal]
  );

  // Income math for a row given (possibly) mixed params
  const computeNOI = useCallback(
    (r) => {
      const size = num(r.size);
      const {
        rent_per_unit_month,
        vacancy_pct,
        opex_mode,
        opex_per_unit_annual,
        opex_pct,
      } = paramsForRow(r);
      const pgi = num(rent_per_unit_month) * 12 * size;
      const egi = pgi * (1 - pctToDec(vacancy_pct));
      let expenses = 0;
      if ((opex_mode || "per_unit") === "pct_egi")
        expenses = egi * pctToDec(opex_pct);
      else expenses = num(opex_per_unit_annual) * size;
      return egi - expenses;
    },
    [paramsForRow]
  );

  // Derived rows (NOI, MV/unit, Multiplier)
  const withCalcs = useMemo(() => {
    return rows.map((r) => {
      const size = num(r.size);
      const mv = num(r.market_value);
      const noi = computeNOI(r);
      const mult = noi > 0 ? mv / noi : 0; // Value ÷ NOI
      const assessedPerUnit = size > 0 ? mv / size : 0;
      return {
        ...r,
        _noi: noi,
        _value_over_noi: mult,
        _assessed_per_unit: assessedPerUnit,
      };
    });
  }, [rows, computeNOI]);

  // Subject + comps USED
  const subject = useMemo(
    () => withCalcs.find((r) => r.id === config.subjectId) || null,
    [withCalcs, config.subjectId]
  );
  const comps = useMemo(
    () => withCalcs.filter((r) => r.id !== config.subjectId),
    [withCalcs, config.subjectId]
  );
  const usedComps = useMemo(() => comps.filter((r) => !!r.use), [comps]);

  // Keep saved map pins in sync with current used comps — remove unchecked, re-label sequentially
  useEffect(() => {
    if (!compMapData?.pins?.length) return;
    const usedIds = new Set(usedComps.map((r) => r.id));
    const subjectPin = compMapData.pins.find((p) => p.isSubject);
    const compPins = compMapData.pins.filter((p) => !p.isSubject && usedIds.has(p.id));
    const relabeled = compPins.map((p, i) => ({ ...p, label: String(i + 1) }));
    const next = [...(subjectPin ? [subjectPin] : []), ...relabeled];
    const prevLabels = compMapData.pins.map((p) => `${p.id}:${p.label}`).join(",");
    const nextLabels = next.map((p) => `${p.id}:${p.label}`).join(",");
    if (prevLabels !== nextLabels) {
      setCompMapData((prev) => ({ ...prev, pins: next }));
    }
  }, [usedComps, compMapData]);

  // Stats (median multiplier × subject NOI)
  const stats = useMemo(() => {
    const multiples = usedComps
      .map((r) => r._value_over_noi || 0)
      .filter((x) => x > 0);
    const med = median(multiples);
    const subjNoi = subject?._noi || 0;
    const indicated = subjNoi * (med || 0);
    return {
      count: usedComps.length,
      median_multiple: med,
      subject_noi: subjNoi,
      indicated_value: indicated,
    };
  }, [usedComps, subject]);

  // ---------- NEW: PGI + stabilized vacancy for Lease-Up ----------
  const pgiAnnual = useMemo(() => {
    if (!subject) return 0;
    const size = num(subject.size);
    const { rent_per_unit_month } = paramsForRow(subject);
    return num(rent_per_unit_month) * 12 * size;
  }, [subject, paramsForRow]);

  const stabilizedVacancyPctNum = useMemo(() => {
    const v = isUniversal("vacancy")
      ? config.baseline.vacancy_pct
      : subject?.ps_vacancy_pct;
    const s = String(v ?? "").replace(/[^0-9.-]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }, [subject, isUniversal, config.baseline]);

  const deductionAmt = useMemo(() => {
    if (deductionType === "lease_up") return Number(leaseUp?.deduction || 0);
    if (deductionType === "manual") return num(manualDeduction);
    return 0;
  }, [deductionType, leaseUp, manualDeduction]);

  const indicatedNet = useMemo(
    () => Math.max(0, (stats.indicated_value || 0) - (deductionAmt || 0)),
    [stats.indicated_value, deductionAmt]
  );

  // Bubble up (persistable JSON)
  const firstMount = useRef(true);
  useEffect(() => {
    if (firstMount.current) firstMount.current = false;
    onChange?.({
      equal_uniform_income: {
        rows,
        config,
        summary: {
          comps_used: stats.count,
          median_value_over_noi: stats.median_multiple,
          subject_noi: stats.subject_noi,
        },
        // NEW: net & gross values plus deduction payload
        indicated_value: indicatedNet,
        indicated_value_before_deductions: stats.indicated_value,
        deduction_type: deductionType,
        manual_deduction: num(manualDeduction || 0),
        lease_up: leaseUp || null,
        pgi_total: pgiAnnual, // for Lease-Up PDF
        vacancy: `${stabilizedVacancyPctNum}%`, // for Lease-Up PDF
        map_data: compMapData || null,
        comp_criteria: { ...compCriteria, unit_noun: unitNoun },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows,
    config,
    stats.count,
    stats.median_multiple,
    stats.subject_noi,
    stats.indicated_value,
    indicatedNet,
    deductionType,
    manualDeduction,
    leaseUp,
    pgiAnnual,
    stabilizedVacancyPctNum,
    compMapData,
    compCriteria,
  ]);

  // Table config
  const C = config.columns;

  const setRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addEmptyComp = () => {
    const row = {
      id: uid(),
      use: true,
      account: "",
      site_name: "",
      address: "",
      property_type: "",
      class: "",
      size: "",
      year_built: "",
      effective_year_built: "",
      nbhd: "",
      market_value: "",
      // property-specific editable fields
      ps_rent_per_unit_month: "",
      ps_vacancy_pct: "",
      ps_opex_mode: "per_unit",
      ps_opex_per_unit_annual: "",
      ps_opex_pct: "",
      notes: "",
    };
    setRows((prev) => [...prev, row]);
  };

  // Auto-detect CAD via cover_meta; include subject + comps, de-duped
  async function doRefreshFromCAD() {
    try {
      setIsRefreshing(true);

      const year = Number(coverCtx.taxYear || new Date().getFullYear());

      // All non-empty accounts (subject + comps), de-duped (uppercased)
      // Expand comma-separated accounts so multi-parcel subjects work
      const allAccounts = Array.from(
        new Set(
          rows
            .flatMap((r) => String(r.account || "").split(",").map((a) => a.trim()))
            .filter((a) => a.length > 0)
            .map((a) => a.toUpperCase())
        )
      );
      if (!allAccounts.length) {
        setRefreshResult({
          variant: "info",
          title: "No Accounts",
          message: "No account numbers found in the grid.",
          failed: [],
        });
        return;
      }

      const resp = await cadRefreshValues({
        year,
        accounts: allAccounts,
        cover_meta: coverCtx.coverMeta,
      });

      // Build per-account value map from server results
      const perAcctMap = new Map(
        (resp?.results || [])
          .filter((r) => r.ok && r.market_value != null)
          .map((r) => [String(r.account).trim().toUpperCase(), Number(r.market_value)])
      );

      // For multi-parcel rows (comma-separated), sum values across all parcels
      // Count BEFORE mutating rows (robust under StrictMode/batching)
      let updatedCount = 0;
      const newRows = rows.map((r) => {
        const raw = String(r.account || "").trim();
        if (!raw) return r;
        const parts = raw.split(",").map((a) => a.trim().toUpperCase()).filter(Boolean);
        const vals = parts.map((p) => perAcctMap.get(p)).filter((v) => v != null);
        if (vals.length) {
          updatedCount++;
          const sum = vals.reduce((a, b) => a + b, 0);
          return { ...r, market_value: String(sum) };
        }
        return r;
      });

      const failed = (resp?.results || [])
        .filter((r) => !r.ok)
        .map((r) => ({
          account: String(r.account || "").trim().toUpperCase(),
          error: r.error || r.reason || "No data returned",
        }));

      setRows(newRows);

      const isHarris = /harris|hcad/i.test(coverCtx?.coverMeta?.property_info?.assessor || "");
      const harrisNote =
        isHarris && updatedCount > 0
          ? "Harris County values are from the initial appraisal roll, not live HCAD data."
          : null;
      setRefreshResult({
        variant: "complete",
        title: "CAD Refresh Complete",
        updated: updatedCount,
        failed,
        harrisNote,
      });
    } catch (err) {
      console.error(err);
      setRefreshResult({
        variant: "error",
        title: "CAD Refresh Failed",
        message: String(err?.message || err),
        failed: [],
      });
    } finally {
      setIsRefreshing(false);
    }
  }

  // UI flags (locks)
  const lockRent = isUniversal("rent");
  const lockVacancy = isUniversal("vacancy");
  const lockOpex = isUniversal("opex");

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => document.getElementById("euinc-file")?.click()}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
        >
          Import CSV / Excel
        </button>
        <input
          id="euinc-file"
          type="file"
          accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={handleFile}
        />

        <button
          onClick={addEmptyComp}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
        >
          + Add Comp
        </button>
        <button
          onClick={() => setCompMapOpen(true)}
          className={`rounded-md border px-3 py-1.5 text-sm ${
            compMapData
              ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-400"
              : "border-slate-300 bg-white hover:border-slate-400"
          }`}
          title={compMapData ? "Edit saved comp map" : "Generate a map from comp addresses"}
        >
          {compMapData ? "\u2713 Edit Map" : "Generate Map"}
        </button>

        {/* Refresh Values + info tooltip + popup */}
        {(() => {
          const list = Array.isArray(supportedCads) ? supportedCads : [];
          const tooltip =
            `Refresh Values pulls “Total Market” using the Packet Year.\n` +
            `Packet Year: ${coverCtx.taxYear}\n\n` +
            `Supported CADs:\n${list.length ? list.join(", ") : "None yet"}`;

          return (
            <>
              <div className="inline-flex items-center gap-1">
                <button
                  onClick={doRefreshFromCAD}
                  disabled={isRefreshing}
                  className={`rounded-md px-3 py-1.5 text-sm border ${
                    isRefreshing
                      ? "border-slate-200 bg-slate-100 text-slate-400"
                      : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-400"
                  }`}
                >
                  {isRefreshing ? "Refreshing…" : "Refresh Values"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowCadInfo(true)}
                  className="h-7 w-7 rounded-full border border-emerald-400 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 flex items-center justify-center text-sm"
                  title={tooltip}
                  aria-label="Refresh Values info"
                >
                  ℹ
                </button>
              </div>

              <RefreshValuesModal
                open={showCadInfo}
                onClose={() => setShowCadInfo(false)}
                supportedCads={list}
                intro={
                  <div className="space-y-3 text-sm text-slate-700">
                    <p>
                      <strong>Refresh Values</strong> pulls{" "}
                      <strong>Total Market</strong> from the CAD website for each
                      account in your grid.
                    </p>
                    <p>
                      Values are fetched using the <strong>Packet Year</strong>.
                      <br />
                      Current Packet Year: <strong>{coverCtx.taxYear}</strong>
                    </p>
                    {/harris|hcad/i.test(coverCtx?.coverMeta?.property_info?.assessor || "") && (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <div className="text-sm font-semibold text-amber-800 mb-1">
                          Harris County Note
                        </div>
                        <p className="text-sm text-amber-700">
                          Harris County values are pulled from the{" "}
                          <strong>initial appraisal roll</strong>, not live HCAD
                          data. These are the values as of the notice date and may
                          differ from the current HCAD website if values have been
                          adjusted.
                        </p>
                      </div>
                    )}
                  </div>
                }
              />
            </>
          );
        })()}

        {/* Columns (no arrow/summary) */}
        <div className="relative ml-auto">
          <button
            onClick={() => setShowPicker((v) => !v)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
          >
            Columns
          </button>
          {showPicker && (
            <div className="absolute right-0 z-10 mt-1 w-80 rounded-lg border border-slate-200 bg-white p-3 shadow">
              <div className="text-xs text-slate-500 mb-2">Show in grid</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                {Object.keys(DEFAULT_COLUMNS).map((k) => (
                  <label key={k} className="flex items-center gap-2">
                    <Checkbox
                      checked={!!C[k]}
                      onChange={(v) =>
                        setConfig((prev) => ({
                          ...prev,
                          columns: { ...prev.columns, [k]: v },
                        }))
                      }
                    />
                    <span>
                      {k === "assessed_per_unit"
                        ? `Assessed ${perUnitLabel}`
                        : k === "noi"
                        ? "NOI (annual)"
                        : k === "value_over_noi"
                        ? "Value ÷ NOI"
                        : k === "nbhd"
                        ? "IMA / Neighborhood"
                        : k.replace(/_/g, " ").replace(/\b\w/g, (m) =>
                            m.toUpperCase()
                          )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mapping dialog */}
      <MappingDialogInc
        open={mapUI.open}
        headers={mapUI.headers}
        sampleRows={mapUI.sample}
        unitNoun={unitNoun}
        initialFieldToIndex={toFieldToIndexInc(mapUI.guessed)}
        onCancel={() =>
          setMapUI({ open: false, headers: [], sample: [], guessed: {} })
        }
        onImport={confirmImport}
      />

      <RefreshResultsDialog
        result={refreshResult}
        onClose={() => setRefreshResult(null)}
        onUncheckFailed={(failedAccts) => {
          const failedSet = new Set(failedAccts.map((a) => a.toUpperCase()));
          setRows((prev) =>
            prev.map((r) => {
              const parts = String(r.account || "")
                .split(",")
                .map((a) => a.trim().toUpperCase())
                .filter(Boolean);
              if (parts.some((p) => failedSet.has(p))) {
                return { ...r, use: false };
              }
              return r;
            })
          );
          setRefreshResult(null);
        }}
      />

      {/* Adjustments panel */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm grid md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <div className="font-medium text-slate-700">Adjustments — Baseline</div>

          <div className="grid grid-cols-2 gap-2">
            {/* Rent */}
            <div className="col-span-2 flex items-center justify-between">
              <div className="text-xs text-slate-500">Rent criterion is</div>
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    name="rentmode"
                    checked={lockRent}
                    onChange={() =>
                      setConfig((p) => ({
                        ...p,
                        baseline: {
                          ...p.baseline,
                          universal: { ...p.baseline.universal, rent: true },
                        },
                      }))
                    }
                  />
                  Universal
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    name="rentmode"
                    checked={!lockRent}
                    onChange={() =>
                      setConfig((p) => ({
                        ...p,
                        baseline: {
                          ...p.baseline,
                          universal: { ...p.baseline.universal, rent: false },
                        },
                      }))
                    }
                  />
                  Property-specific
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">{rentLabel}</label>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={config.baseline.rent_per_unit_month}
                onChange={(e) =>
                  setConfig((p) => ({
                    ...p,
                    baseline: {
                      ...p.baseline,
                      rent_per_unit_month: e.target.value.replace(/[^\d.]/g, ""),
                    },
                  }))
                }
                inputMode="decimal"
                placeholder="e.g. 1200"
                disabled={!lockRent}
                title={
                  lockRent
                    ? "Universal baseline"
                    : "Disabled (property-specific in table)"
                }
              />
            </div>

            {/* Vacancy */}
            <div className="col-span-2 flex items-center justify-between mt-2">
              <div className="text-xs text-slate-500">Vacancy criterion is</div>
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    name="vacmode"
                    checked={lockVacancy}
                    onChange={() =>
                      setConfig((p) => ({
                        ...p,
                        baseline: {
                          ...p.baseline,
                          universal: { ...p.baseline.universal, vacancy: true },
                        },
                      }))
                    }
                  />
                  Universal
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    name="vacmode"
                    checked={!lockVacancy}
                    onChange={() =>
                      setConfig((p) => ({
                        ...p,
                        baseline: {
                          ...p.baseline,
                          universal: { ...p.baseline.universal, vacancy: false },
                        },
                      }))
                    }
                  />
                  Property-specific
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Vacancy %</label>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={config.baseline.vacancy_pct}
                onChange={(e) =>
                  setConfig((p) => ({
                    ...p,
                    baseline: {
                      ...p.baseline,
                      vacancy_pct: e.target.value.replace(/[^\d.%-]/g, ""),
                    },
                  }))
                }
                inputMode="decimal"
                placeholder="5%"
                disabled={!lockVacancy}
                title={
                  lockVacancy
                    ? "Universal baseline"
                    : "Disabled (property-specific in table)"
                }
              />
            </div>

            {/* Opex */}
            <div className="col-span-2 flex items-center justify-between mt-2">
              <div className="text-xs text-slate-500">Opex criterion is</div>
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    name="opexmode"
                    checked={lockOpex}
                    onChange={() =>
                      setConfig((p) => ({
                        ...p,
                        baseline: {
                          ...p.baseline,
                          universal: { ...p.baseline.universal, opex: true },
                        },
                      }))
                    }
                  />
                  Universal
                </label>
                <label className="inline-flex items-center gap-1">
                  <input
                    type="radio"
                    name="opexmode"
                    checked={!lockOpex}
                    onChange={() =>
                      setConfig((p) => ({
                        ...p,
                        baseline: {
                          ...p.baseline,
                          universal: { ...p.baseline.universal, opex: false },
                        },
                      }))
                    }
                  />
                  Property-specific
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Opex Mode</label>
              <select
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={config.baseline.opex_mode}
                onChange={(e) =>
                  setConfig((p) => ({
                    ...p,
                    baseline: { ...p.baseline, opex_mode: e.target.value },
                  }))
                }
                disabled={!lockOpex}
                title={
                  lockOpex
                    ? "Universal baseline"
                    : "Disabled (property-specific in table)"
                }
              >
                <option value="per_unit">$ / {unitNoun} / year</option>
                <option value="pct_egi">% of EGI</option>
              </select>
            </div>

            {config.baseline.opex_mode === "pct_egi" ? (
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Opex % of EGI
                </label>
                <input
                  className="w-full rounded border border-slate-300 px-2 py-1"
                  value={config.baseline.opex_pct}
                  onChange={(e) =>
                    setConfig((p) => ({
                      ...p,
                      baseline: {
                        ...p.baseline,
                        opex_pct: e.target.value.replace(/[^\d.%-]/g, ""),
                      },
                    }))
                  }
                  inputMode="decimal"
                  placeholder="35%"
                  disabled={!lockOpex}
                />
              </div>
            ) : (
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Opex $/{unitNoun}/yr
                </label>
                <input
                  className="w-full rounded border border-slate-300 px-2 py-1"
                  value={config.baseline.opex_per_unit_annual}
                  onChange={(e) =>
                    setConfig((p) => ({
                      ...p,
                      baseline: {
                        ...p.baseline,
                        opex_per_unit_annual: e.target.value.replace(/[^\d.]/g, ""),
                      },
                    }))
                  }
                  inputMode="decimal"
                  placeholder="4800"
                  disabled={!lockOpex}
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="space-y-1">
          <div className="font-medium text-slate-700">Summary</div>
          <div>
            <span className="text-slate-500">Comps used:</span>{" "}
            <span className="font-medium">{stats.count}</span>
          </div>
          <div>
            <span className="text-slate-500">Median Value ÷ NOI:</span>{" "}
            <span className="font-medium">{fmtMult(stats.median_multiple)}</span>
          </div>
          <div>
            <span className="text-slate-500">Subject NOI:</span>{" "}
            <span className="font-medium">{fmtMoney0(stats.subject_noi)}</span>
          </div>

          {/* NEW: show deduction + net */}
          {deductionAmt > 0 && (
            <div>
              <span className="text-slate-500">Less deduction:</span>{" "}
              <span className="font-medium">
                {fmtMoney0(deductionAmt)}{" "}
                ({deductionType === "lease_up" ? "Lease-Up" : "Manual"})
              </span>
            </div>
          )}
          <div>
            <span className="text-slate-500">Indicated Value (net):</span>{" "}
            <span className="font-semibold">{fmtMoney0(indicatedNet)}</span>
          </div>
        </div>
      </div>

      {/* Comp selection criteria */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm space-y-3">
        <div className="font-semibold text-slate-800">Comp Selection Criteria</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nbhd / Area</label>
            <input
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={compCriteria.nbhd_area}
              onChange={(e) => updateCriteria({ nbhd_area: e.target.value })}
              placeholder="e.g., 5944.01"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Radius (miles)</label>
            <div className="flex items-center gap-1">
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.radius}
                onChange={(e) => updateCriteria({ radius: e.target.value.replace(/[^\d.]/g, "").replace(/^(\d*\.?\d{0,2}).*$/, "$1") })}
                placeholder="e.g., 5"
                inputMode="decimal"
              />
              <span className="text-sm text-slate-500 whitespace-nowrap">miles</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type</label>
            <input
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={compCriteria.type}
              onChange={(e) => updateCriteria({ type: e.target.value })}
              placeholder="e.g., Multifamily"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Age (Year Built range)</label>
            <div className="flex items-center gap-1">
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.age_min}
                onChange={(e) => updateCriteria({ age_min: e.target.value.replace(/[^\d]/g, "") })}
                placeholder="From"
                inputMode="numeric"
              />
              <span className="text-slate-400">–</span>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.age_max}
                onChange={(e) => updateCriteria({ age_max: e.target.value.replace(/[^\d]/g, "") })}
                placeholder="To"
                inputMode="numeric"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Size ({unitNoun} range)</label>
            <div className="flex items-center gap-1">
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.size_min}
                onChange={(e) => updateCriteria({ size_min: e.target.value.replace(/[^\d.]/g, "") })}
                placeholder="Min"
                inputMode="decimal"
              />
              <span className="text-slate-400">–</span>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={compCriteria.size_max}
                onChange={(e) => updateCriteria({ size_max: e.target.value.replace(/[^\d.]/g, "") })}
                placeholder="Max"
                inputMode="decimal"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Class</label>
            <input
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={compCriteria.class}
              onChange={(e) => updateCriteria({ class: e.target.value })}
              placeholder="e.g., B, C"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Other</label>
          <input
            className="w-full rounded border border-slate-300 px-2 py-1"
            value={compCriteria.other || ""}
            onChange={(e) => updateCriteria({ other: e.target.value })}
            placeholder="Additional criteria..."
          />
        </div>
        <label className="flex items-center gap-2 pt-1">
          <Checkbox
            checked={!!compCriteria.none_omitted}
            onChange={(v) => updateCriteria({ none_omitted: v })}
          />
          <span className="text-sm text-slate-700">None omitted — all comps meeting these criteria were included</span>
        </label>
      </div>

      {/* NEW: Deductions card */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm space-y-3">
        <div className="font-semibold text-slate-800">Deductions (optional)</div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Deduction Type</label>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1"
              value={deductionType}
              onChange={(e) => setDeductionType(e.target.value)}
            >
              <option value="none">None</option>
              <option value="lease_up">Lease-Up (NPV)</option>
              <option value="manual">Manual ($)</option>
            </select>
          </div>

          {deductionType === "manual" && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Manual Deduction ($)
              </label>
              <input
                className="w-full rounded border border-slate-300 px-2 py-1"
                value={manualDeduction}
                onChange={(e) =>
                  setManualDeduction(e.target.value.replace(/[^\d.-]/g, ""))
                }
                inputMode="decimal"
                placeholder="e.g., 250000"
              />
            </div>
          )}

          {deductionType === "lease_up" && (
            <div className="text-xs text-slate-600 sm:col-span-1">
              <div>
                PGI (annual): <strong>{fmtMoney0(pgiAnnual)}</strong>
              </div>
              <div>
                Stabilized Vacancy:{" "}
                <strong>
                  {stabilizedVacancyPctNum.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                  %
                </strong>
              </div>
            </div>
          )}
        </div>

        {deductionType === "lease_up" && (
          <LeaseUpCalculator
            value={leaseUp || undefined}
            onChange={setLeaseUp}
            pgiAnnual={pgiAnnual}
            opexAnnual={0}
            stabilizedVacancyPct={stabilizedVacancyPctNum}
            defaultDiscountAnnualPct={
              stats.median_multiple ? 100 / stats.median_multiple : 0
            }
            totalBuildingSF={coverCtx.unitType === "SF" ? coverCtx.sizeFromCover : 0}
          />
        )}
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-xl border border-slate-200">
        <table className="min-w-[1800px] w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left sticky left-0 bg-slate-50 z-10 w-[140px]">
                Role
              </th>
              {C.use && (
                <th className="px-2 py-2 text-left sticky left-[140px] bg-slate-50 z-10">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Check / uncheck all comps">
                    <Checkbox
                      checked={comps.length > 0 && comps.every((r) => !!r.use)}
                      onChange={(v) => setRows((prev) => prev.map((r) => r.id === config.subjectId ? r : { ...r, use: v }))}
                    />
                    <span>Use</span>
                  </label>
                </th>
              )}
              {C.account && <th className="px-2 py-2 text-left">Account #</th>}
              {C.site_name && <th className="px-2 py-2 text-left">Site Name</th>}
              {C.address && <th className="px-2 py-2 text-left">Address</th>}
              {C.property_type && (
                <th className="px-2 py-2 text-left">Property Type</th>
              )}
              {C.class && <th className="px-2 py-2 text-left">Class</th>}
              {C.size && (
                <th className="px-2 py-2 text-right">Size ({unitNoun})</th>
              )}
              {C.year_built && (
                <th className="px-2 py-2 text-right">Year Built</th>
              )}
              {C.effective_year_built && (
                <th className="px-2 py-2 text-right">Effective YB</th>
              )}
              {C.nbhd && (
                <th className="px-2 py-2 text-left">IMA / Neighborhood</th>
              )}
              {C.market_value && (
                <th className="px-2 py-2 text-right">Total Value</th>
              )}
              {C.assessed_per_unit && (
                <th className="px-2 py-2 text-right">Assessed {perUnitLabel}</th>
              )}
              {C.noi && (
                <th className="px-2 py-2 text-right">NOI (annual)</th>
              )}
              {C.value_over_noi && (
                <th className="px-2 py-2 text-right">Value ÷ NOI</th>
              )}
              {C.notes && <th className="px-2 py-2 text-left">Notes</th>}
              <th className="px-2 py-2"></th>
            </tr>
          </thead>

          <tbody>
            {withCalcs.map((r) => {
              const isSubject = r.id === config.subjectId;
              return (
                <tr key={r.id} className="border-t">
                  {/* Role */}
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 w-[140px]">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="subject"
                        checked={isSubject}
                        onChange={() =>
                          setConfig((p) => ({ ...p, subjectId: r.id }))
                        }
                        title="Set as Subject"
                      />
                      <span className={isSubject ? "font-medium" : ""}>
                        {isSubject ? "Subject" : "Comp"}
                      </span>
                    </label>
                  </td>

                  {C.use && (
                    <td className="px-2 py-1 sticky left-[140px] bg-white z-10">
                      <Checkbox
                        checked={!!r.use}
                        onChange={(v) => setRow(r.id, { use: v })}
                        disabled={isSubject}
                      />
                    </td>
                  )}

                  {C.account && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.account || ""}
                        onChange={(e) => setRow(r.id, { account: e.target.value })}
                      />
                    </td>
                  )}
                  {C.site_name && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.site_name || ""}
                        onChange={(e) => setRow(r.id, { site_name: e.target.value })}
                      />
                    </td>
                  )}
                  {C.address && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.address || ""}
                        onChange={(e) => setRow(r.id, { address: e.target.value })}
                      />
                    </td>
                  )}
                  {C.property_type && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.property_type || ""}
                        onChange={(e) =>
                          setRow(r.id, { property_type: e.target.value })
                        }
                      />
                    </td>
                  )}
                  {C.class && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.class || ""}
                        onChange={(e) => setRow(r.id, { class: e.target.value })}
                      />
                    </td>
                  )}
                  {C.size && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell
                        value={r.size}
                        onChange={(v) =>
                          setRow(r.id, { size: v.replace(/[^\d.]/g, "") })
                        }
                        inputMode="decimal"
                      />
                    </td>
                  )}
                  {C.year_built && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell
                        value={r.year_built}
                        onChange={(v) =>
                          setRow(r.id, { year_built: v.replace(/[^\d]/g, "") })
                        }
                        inputMode="numeric"
                      />
                    </td>
                  )}
                  {C.effective_year_built && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell
                        value={r.effective_year_built}
                        onChange={(v) =>
                          setRow(r.id, {
                            effective_year_built: v.replace(/[^\d]/g, ""),
                          })
                        }
                        inputMode="numeric"
                      />
                    </td>
                  )}
                  {C.nbhd && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.nbhd || ""}
                        onChange={(e) => setRow(r.id, { nbhd: e.target.value })}
                      />
                    </td>
                  )}
                  {C.market_value && (
                    <td className="px-2 py-1 text-right">
                      <NumberCell
                        value={r.market_value}
                        onChange={(v) =>
                          setRow(r.id, {
                            market_value: v.replace(/[^\d.-]/g, ""),
                          })
                        }
                        inputMode="decimal"
                      />
                    </td>
                  )}

                  {C.assessed_per_unit && (
                    <td className="px-2 py-1 text-right font-medium">
                      {fmtMoney2(r._assessed_per_unit)}
                    </td>
                  )}
                  {C.noi && (
                    <td className="px-2 py-1 text-right font-medium">
                      {fmtMoney0(r._noi)}
                    </td>
                  )}
                  {C.value_over_noi && (
                    <td className="px-2 py-1 text-right font-medium">
                      {fmtMult(r._value_over_noi)}
                    </td>
                  )}

                  {C.notes && (
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.notes || ""}
                        onChange={(e) => setRow(r.id, { notes: e.target.value })}
                      />
                    </td>
                  )}

                  <td className="px-2 py-1">
                    {!isSubject && (
                      <button
                        onClick={() => removeRow(r.id)}
                        className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:border-rose-400"
                        title="Remove comp"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Property-specific editors (show only when universal is OFF) */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="text-sm font-semibold text-slate-800 mb-2">
          Property-Specific Inputs
        </div>
        <div className="text-xs text-slate-500 mb-3">
          If a criterion is set to <b>Property-specific</b> above, enter values here per
          row. Subject values participate in NOI and indicated value (via multiplier
          median).
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-1 text-left w-48">Property (Site Name)</th>
                {!lockRent && (
                  <th className="px-2 py-1 text-right w-28 whitespace-nowrap">
                    {rentLabel}
                  </th>
                )}
                {!lockVacancy && (
                  <th className="px-2 py-1 text-right w-24 whitespace-nowrap">
                    Vacancy %
                  </th>
                )}
                {!lockOpex && (
                  <th className="px-2 py-1 text-left w-32 whitespace-nowrap">Opex Mode</th>
                )}
                {!lockOpex && (
                  <th className="px-2 py-1 text-right w-28 whitespace-nowrap">{`Opex $/${unitNoun}/yr`}</th>
                )}
                {!lockOpex && (
                  <th className="px-2 py-1 text-right w-28 whitespace-nowrap">
                    Opex % of EGI
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isSubject = r.id === config.subjectId;
                return (
                  <tr key={r.id} className="border-t">
                    <td className="px-2 py-1 w-48">
                      <div className="truncate">
                        {r.site_name ||
                          r.address ||
                          r.account ||
                          (isSubject ? "Subject" : "Comp")}
                      </div>
                    </td>

                    {!lockRent && (
                      <td className="px-2 py-1 text-right">
                        <NumberCell
                          value={r.ps_rent_per_unit_month}
                          onChange={(v) =>
                            setRow(r.id, {
                              ps_rent_per_unit_month: v.replace(/[^\d.]/g, ""),
                            })
                          }
                          inputMode="decimal"
                        />
                      </td>
                    )}

                    {!lockVacancy && (
                      <td className="px-2 py-1 text-right">
                        <NumberCell
                          value={r.ps_vacancy_pct}
                          onChange={(v) =>
                            setRow(r.id, {
                              ps_vacancy_pct: v.replace(/[^\d.%-]/g, ""),
                            })
                          }
                          suffix="%"
                          inputMode="decimal"
                        />
                      </td>
                    )}

                    {!lockOpex && (
                      <>
                        <td className="px-2 py-1">
                          <select
                            className="w-full rounded border border-slate-300 px-2 py-1"
                            value={r.ps_opex_mode || "per_unit"}
                            onChange={(e) =>
                              setRow(r.id, { ps_opex_mode: e.target.value })
                            }
                          >
                            <option value="per_unit">$ / {unitNoun} / year</option>
                            <option value="pct_egi">% of EGI</option>
                          </select>
                        </td>
                        <td className="px-2 py-1 text-right">
                          <NumberCell
                            value={r.ps_opex_per_unit_annual}
                            onChange={(v) =>
                              setRow(r.id, {
                                ps_opex_per_unit_annual: v.replace(/[^\d.]/g, ""),
                              })
                            }
                            inputMode="decimal"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <NumberCell
                            value={r.ps_opex_pct}
                            onChange={(v) =>
                              setRow(r.id, {
                                ps_opex_pct: v.replace(/[^\d.%-]/g, ""),
                              })
                            }
                            suffix="%"
                            inputMode="decimal"
                          />
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Comp Map Modal */}
      <CompMapModal
        open={compMapOpen}
        onClose={() => setCompMapOpen(false)}
        onSave={(md) => {
          setCompMapData(md);
          setCompMapOpen(false);
          const payload = {
            equal_uniform_income: {
              rows,
              config,
              summary: {
                comps_used: stats.count,
                median_value_over_noi: stats.median_multiple,
                subject_noi: stats.subject_noi,
              },
              indicated_value: indicatedNet,
              indicated_value_before_deductions: stats.indicated_value,
              deduction_type: deductionType,
              manual_deduction: num(manualDeduction || 0),
              lease_up: leaseUp || null,
              pgi_total: pgiAnnual,
              vacancy: `${stabilizedVacancyPctNum}%`,
              map_data: md,
              comp_criteria: { ...compCriteria, unit_noun: unitNoun },
            },
          };
          onSave?.(payload);
        }}
        rows={rows}
        subjectId={config.subjectId}
        assessor={coverCtx?.coverMeta?.property_info?.assessor || ""}
        state={coverCtx?.coverMeta?.property_info?.state || ""}
        existingMapData={compMapData}
      />
    </div>
  );
}
